import { useAdmin } from '../../../contexts/AdminContext'
import React, { useEffect, useMemo, useState } from 'react'
import Heading from '../../../components/Heading'
import Text from '../../../components/Text'
import IconButton from '../../../components/IconButton'
import {
    faArrowLeft,
    faCheck,
    faCreditCard,
} from '@fortawesome/free-solid-svg-icons'
import Card from '../../../components/Card'
import Checkbox from '../../../components/Checkbox'
import Button from '../../../components/Button'
import { Payment, Plan } from '../../../types/Subscription'
import Icon from '../../../components/Icon'
import { faCcPaypal } from '@fortawesome/free-brands-svg-icons'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'
import styles from './SubscriptionSubscribe.module.css'
import Loader from '../../../components/Loader'
import { showToast } from '../../../services/ToastService'
import { useNavigate } from 'react-router-dom'
import { usePluginContext } from '../../../contexts/PluginContext'

const SubscriptionSubscribe: React.FC = () => {
    const { service, paymentMethods, plans, getPlans, getSubscriptions } =
        useAdmin()
    const { plugin } = usePluginContext()
    const [selection, setSelection] = useState<Plan[]>([])
    const [isLoading, setLoading] = useState<boolean>(false)
    const [paymentMethod, setPaymentMethod] = useState<Payment | null>(
        paymentMethods.length > 0 ? paymentMethods[0] : null
    )
    const { t } = useTranslation(['settings'])
    const navigate = useNavigate()

    useEffect(() => {
        getPlans()
    }, [getPlans])
    const changeSelection = (plan: Plan) => {
        setSelection((prev) => {
            if (prev.map((p) => p.id).includes(plan.id)) {
                return prev.filter(
                    (p) => p.id !== plan.id && p.dependent_id !== plan.id
                )
            } else {
                if (
                    (!!plan.dependent_id &&
                        prev.map((p) => p.id).includes(plan.dependent_id)) ||
                    !plan.dependent_id
                ) {
                    return [...prev, plan]
                } else {
                    return prev
                }
            }
        })
    }

    const total = useMemo(() => {
        if (selection.length > 0) {
            const sum = selection
                .map((item) => item.price * 100)
                .reduce((sum, p) => sum + p)

            const total = ((sum / 100).toFixed(2) + ' €').replace('.', ',')
            const ust = (((sum / 100) * 0.19).toFixed(2) + ' €').replace(
                '.',
                ','
            )

            const all = (((sum / 100) * 1.19).toFixed(2) + ' €').replace(
                '.',
                ','
            )

            return (
                <p>
                    <span>Zwischensumme: {total}</span>
                    <br />
                    <span>MwSt.: {ust}</span>
                    <br />
                    <b>Gesamtsumme: {all}</b>
                </p>
            )
        }
        return (
            <p>
                <span>Zwischensumme: 0,00 €</span>
                <br />
                <span>MwSt.: 0,00 €</span>
                <br />
                <b>Gesamtsumme: 0,00 €</b>
            </p>
        )
    }, [selection])

    const subscribe = () => {
        setLoading(true)
        if (paymentMethod && plugin)
            service
                .subscribe({
                    paymentMethod: paymentMethod.id,
                    plans: selection.map((s) => s.stripe_plan),
                    host: window.location.host,
                    plugin: plugin.plugin,
                })
                .then((res) => {
                    if (res.status === 200 || res.status === 201) {
                        getSubscriptions()
                        showToast(t('subscription.licenseSuccessfullyBooked'))
                        navigate('/subscription')
                    }
                    setLoading(false)
                })
    }

    return (
        <div className={'zr-position-relative zr-mt-3'}>
            <Heading level={3} classes={'zr-mb-4'}>
                {t('subscription.bookNewLicense')}
            </Heading>
            <IconButton
                icon={faArrowLeft}
                color={'black'}
                onClick={() => navigate('/subscription')}
            >
                {t('subscription.back')}
            </IconButton>
            <Text classes={'zr-mt-3'}>{t('subscription.selectPackages')}</Text>
            {plans.map((plan) => {
                return (
                    <Card key={plan.id} classes={'zr-my-3'}>
                        <div className={cx(styles['planContent'])}>
                            <div>
                                <Heading level={4}>
                                    {plan.dependent_id
                                        ? plan.name + ' (Erweiterung)'
                                        : plan.name}
                                </Heading>
                                <p>{plan.description}</p>
                            </div>
                            <div className={cx(styles['checkboxContainer'])}>
                                <Checkbox
                                    property={'plan'}
                                    value={selection
                                        .map((p) => p.id)
                                        .includes(plan.id)}
                                    label={plan.price_currency}
                                    onChange={() => changeSelection(plan)}
                                />
                            </div>
                        </div>
                    </Card>
                )
            })}
            {total}
            <Heading level={4}>{t('subscription.selectPaymentMethod')}</Heading>
            <div className={cx(styles['wrapperContainer'])}>
                {paymentMethods.map((p) => {
                    return (
                        <Card
                            key={p.id}
                            onClick={() => setPaymentMethod(p)}
                            style={{
                                background:
                                    paymentMethod?.id === p.id
                                        ? '#F5F5F5'
                                        : '#FFF',
                            }}
                            classes={cx(styles['paymentCard'])}
                        >
                            <div className={cx(styles['paymentContent'])}>
                                {p.type === 'paypal' && p.paypal && (
                                    <div className={cx(styles['paymentIcon'])}>
                                        <Icon icon={faCcPaypal} size={'2rem'} />
                                        <Text>{p.paypal.payer_email}</Text>
                                    </div>
                                )}
                                {p.type === 'card' && p.card && (
                                    <div className={cx(styles['paymentIcon'])}>
                                        <Icon
                                            icon={faCreditCard}
                                            size={'2rem'}
                                        />
                                        <Text>
                                            {'**** **** **** ' + p.card.last4}
                                        </Text>
                                    </div>
                                )}
                                {paymentMethod?.id === p.id && (
                                    <Icon icon={faCheck} size={'2rem'} />
                                )}
                            </div>
                        </Card>
                    )
                })}
            </div>

            {!isLoading ? (
                <Button
                    disabled={selection.length === 0 || !paymentMethod}
                    classes={'zr-mb-6'}
                    hierarchy={'primary'}
                    text={t('subscription.continue')}
                    fn={() => subscribe()}
                />
            ) : (
                <div className={'zr-mb-6'}>
                    <Loader isLoading={true} />
                </div>
            )}
        </div>
    )
}

export default SubscriptionSubscribe
